import { HttpApiService } from "@/shared/services/httpApi.service";

import { ACCESS_ENDPOINTS } from "../constants/accessCatalog.constants";
import type { AssignedRole } from "../types/access.types";

/**
 * Role assignment for principals (users now; API keys reuse the same drawer in a later deliverable).
 * Endpoints and shapes come straight from the backend contract: assigning returns the full new
 * `AssignedRole[]`; removing is 204.
 *
 * Assigning requires `users:update` + strict `roles:update`; removing the same.
 */
export const principalRolesService = {
  listUserRoles: async (userId: string): Promise<AssignedRole[]> => {
    const response = await HttpApiService.get<AssignedRole[]>(ACCESS_ENDPOINTS.userRoles(userId));
    return response.data;
  },

  /** `POST /internal/users/:id/roles { role_ids }` → the user's full new role set. */
  assignUserRoles: async (userId: string, roleIds: string[]): Promise<AssignedRole[]> => {
    const response = await HttpApiService.post<AssignedRole[], { role_ids: string[] }>(
      ACCESS_ENDPOINTS.userRoles(userId),
      { role_ids: roleIds },
    );
    return response.data;
  },

  removeUserRole: async (userId: string, roleId: string): Promise<void> => {
    await HttpApiService.delete(ACCESS_ENDPOINTS.userRole(userId, roleId));
  },

  listApiKeyRoles: async (keyId: string): Promise<AssignedRole[]> => {
    const response = await HttpApiService.get<AssignedRole[]>(ACCESS_ENDPOINTS.apiKeyRoles(keyId));
    return response.data;
  },

  /** `POST /api-keys/:id/roles { role_ids }` → the key's full new role set. */
  assignApiKeyRoles: async (keyId: string, roleIds: string[]): Promise<AssignedRole[]> => {
    const response = await HttpApiService.post<AssignedRole[], { role_ids: string[] }>(
      ACCESS_ENDPOINTS.apiKeyRoles(keyId),
      { role_ids: roleIds },
    );
    return response.data;
  },

  removeApiKeyRole: async (keyId: string, roleId: string): Promise<void> => {
    await HttpApiService.delete(ACCESS_ENDPOINTS.apiKeyRole(keyId, roleId));
  },
};
